import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { userProfileService } from "@/services/userProfileService";
import {
  LIBRARY_RECENT_CHANGED_EVENT,
  readRecentDocuments,
  touchRecentDocument,
  type LibraryDocRef,
  type LibraryRecentEntry,
} from "@/lib/libraryQuickAccess";

export const LIBRARY_FAVORITES_QUERY_KEY = ["library-favorites"] as const;
export const LIBRARY_RECENT_QUERY_KEY = ["library-recent"] as const;

function subscribeRecent(userId: string | undefined, onStoreChange: () => void) {
  if (!userId || typeof window === "undefined") return () => {};
  const onChanged = (e: Event) => {
    const detail = (e as CustomEvent<{ userId?: string }>).detail;
    if (!detail?.userId || detail.userId === userId) onStoreChange();
  };
  const onStorage = (ev: StorageEvent) => {
    if (ev.key?.startsWith("library-recent-")) onStoreChange();
  };
  window.addEventListener(LIBRARY_RECENT_CHANGED_EVENT, onChanged);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(LIBRARY_RECENT_CHANGED_EVENT, onChanged);
    window.removeEventListener("storage", onStorage);
  };
}

export function useLibraryFavorites() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: favorites = [], isLoading } = useQuery({
    queryKey: [...LIBRARY_FAVORITES_QUERY_KEY, user?.id],
    queryFn: () => userProfileService.getLibraryFavorites(),
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const favoriteIdSet = useMemo(() => new Set(favorites.map((f) => f.id)), [favorites]);

  const isFavorite = useCallback(
    (id: string, itemType?: "note" | "reference") => {
      if (itemType) {
        return favorites.some((f) => f.id === id && f.itemType === itemType);
      }
      return favoriteIdSet.has(id);
    },
    [favorites, favoriteIdSet]
  );

  const toggleMutation = useMutation({
    mutationFn: (ref: LibraryDocRef) => userProfileService.toggleLibraryFavorite(ref),
    onSuccess: (updated) => {
      queryClient.setQueryData([...LIBRARY_FAVORITES_QUERY_KEY, user?.id], updated);
      userProfileService.clearCache();
    },
  });

  const toggleFavorite = useCallback(
    (ref: LibraryDocRef) => toggleMutation.mutateAsync(ref),
    [toggleMutation]
  );

  return {
    favorites,
    favoriteIdSet,
    isFavorite,
    toggleFavorite,
    isLoading,
    isToggling: toggleMutation.isPending,
  };
}

export function useLibraryRecent() {
  const { user } = useAuth();
  const userId = user?.id;

  const recent = useSyncExternalStore(
    (onStoreChange) => subscribeRecent(userId, onStoreChange),
    () => readRecentDocuments(userId),
    () => readRecentDocuments(userId)
  );

  const recentIds = useMemo(() => recent.map((e) => e.id), [recent]);

  const touchRecent = useCallback(
    (ref: LibraryDocRef) => {
      return touchRecentDocument(userId, ref);
    },
    [userId]
  );

  return {
    recent,
    recentIds,
    touchRecent,
  };
}

export type ResolvedQuickAccessDoc = LibraryDocRef & {
  title: string;
};

export function resolveQuickAccessDocs(
  refs: LibraryDocRef[],
  kbItems: Array<{ id: string; title?: string | null }>,
  sources: Array<{ id: string; title?: string | null }>
): ResolvedQuickAccessDoc[] {
  const titleById = new Map<string, string>();
  for (const item of kbItems) {
    titleById.set(item.id, item.title?.trim() || "Untitled");
  }
  for (const source of sources) {
    titleById.set(source.id, source.title?.trim() || "Untitled Reference");
  }
  return refs
    .filter((ref) => titleById.has(ref.id))
    .map((ref) => ({
      ...ref,
      title: titleById.get(ref.id)!,
    }));
}

export function orderByRecentIds<T extends { id: string }>(
  items: T[],
  recentIds: string[]
): T[] {
  const order = new Map(recentIds.map((id, i) => [id, i]));
  return [...items].sort((a, b) => {
    const ai = order.get(a.id) ?? 999;
    const bi = order.get(b.id) ?? 999;
    return ai - bi;
  });
}

export type { LibraryDocRef, LibraryRecentEntry };
